import {useEffect} from 'react'
import {useTranslate} from '@tolgee/react'
import monitoring from '../utils/monitoring'
import {capture_event} from '../lib/posthog'

/**
 * "Copy the promo code" links for marketing's PostHog popups.
 *
 * PostHog sanitizes survey HTML when it is saved: <button>, onclick, class and
 * data-* are stripped, but <a href> survives. So a popup marks a copy button as
 * a link to `#kopirovat-<CODE>`, and this one page-wide listener does the copy.
 * The popup renders in an open shadow root, hence composedPath() rather than
 * event.target, which is retargeted to the shadow host at document level.
 *
 * Only copies text: the code is still typed into checkout and checked there.
 */
const COPY_HREF = /^#kopirovat-([A-Za-z0-9_-]{1,40})$/
const COPIED_LABEL_MS = 2000

export function find_promo_copy_link(
  event: Event,
): {link: HTMLAnchorElement; code: string} | null {
  for (const node of event.composedPath()) {
    if (!(node instanceof HTMLAnchorElement)) continue
    const match = COPY_HREF.exec(node.getAttribute('href') ?? '')
    return match ? {link: node, code: match[1]} : null
  }
  return null
}

// The link's own content while it shows the "copied" label. Kept per link so a
// second tap within the 2 s neither saves the label as the original nor leaves
// it stuck, and as nodes so any markup inside the link comes back intact.
const showing_copied = new WeakMap<
  HTMLAnchorElement,
  {original: Node[]; timer: number}
>()

function show_copied(link: HTMLAnchorElement, label: string) {
  const current = showing_copied.get(link)
  if (current) window.clearTimeout(current.timer)
  const original = current?.original ?? Array.from(link.childNodes)
  link.textContent = label
  const timer = window.setTimeout(() => {
    link.replaceChildren(...original)
    showing_copied.delete(link)
  }, COPIED_LABEL_MS)
  showing_copied.set(link, {original, timer})
}

export default function PromoCopyLinks() {
  const {t} = useTranslate()

  useEffect(() => {
    const on_click = (event: MouseEvent) => {
      const found = find_promo_copy_link(event)
      if (!found) return
      event.preventDefault()
      const {link, code} = found
      const copy = navigator.clipboard?.writeText(code)
      if (!copy) {
        monitoring.warning('promo_copy:clipboard_unavailable', {code})
        return
      }
      copy.then(
        () => {
          capture_event('promo_code_copied', {code})
          show_copied(link, t('myAccount.coupons.copied'))
        },
        (error) =>
          monitoring.warning('promo_copy:clipboard_failed', {
            code,
            error: String(error),
          }),
      )
    }
    document.addEventListener('click', on_click)
    return () => document.removeEventListener('click', on_click)
  }, [t])

  return null
}
